import { useCallback, useEffect, useRef, useState } from 'react';

import { logToSentry, useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { trackCartItemCount, trackCartPreviewRebuild } from '@jsmdg/tracking';
import { CustomUIEventEnum } from '@jsmdg/ui-event';
import { isSSR } from '@jsmdg/yoshi';
import { type CartPreview } from '../../shared/types';
import { cartCache } from '../helper/cartCache';

let cartPromise: Promise<CartPreview> | null = null;

function useCart(): CartPreview | null {
    const [cart, setCart] = useState<CartPreview | null>(null);
    const { fetchCart }: { fetchCart: () => Promise<CartPreview> } = useFragmentContext();
    const hasFetched = useRef(false);

    const fetch = useCallback(
        async (forceRefetch: boolean = false): Promise<void> => {
            if (hasFetched.current && !forceRefetch) {
                return;
            }

            if (forceRefetch) {
                hasFetched.current = false;
                cartPromise = null;
                cartCache.clear();
            }

            /*
             * if users have cookie disabled, it doesn't make sense to fetch the cart which is session related
             */
            if (!cartPromise && navigator.cookieEnabled) {
                hasFetched.current = true;
                cartPromise = fetchCart();

                try {
                    const fetchedCart = await cartPromise;
                    if (
                        fetchedCart.itemsCount > 0 &&
                        fetchedCart.rebuilt &&
                        fetchedCart.trackingLabel
                    ) {
                        trackCartPreviewRebuild(fetchedCart.trackingLabel);
                    }

                    trackCartItemCount(fetchedCart.itemsCount);
                    setCart(fetchedCart);
                } catch (error) {
                    logToSentry(error as Error);
                    setCart(null);
                }
            } else if (cartPromise) {
                try {
                    setCart(await cartPromise);
                } catch (error) {
                    logToSentry(error as Error);
                    setCart(null);
                }
            }
        },
        [fetchCart],
    );

    useEffect(() => {
        if (isSSR) {
            return undefined;
        }

        fetch();

        const handleFetchCart = (): void => {
            fetch(true);
        };

        document.addEventListener(CustomUIEventEnum.FetchCart, handleFetchCart);

        return () => {
            document.removeEventListener(CustomUIEventEnum.FetchCart, handleFetchCart);
        };
    }, [fetch]);

    return cart;
}

export { useCart };
